import { LoaderCircle, X } from 'lucide-react'
import { useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'

function BodegaModal({ bodega, onClose, onGuardado }) {
  const { empresaActual } = useEmpresa()
  const esEdicion = Boolean(bodega)

  const [codigo, setCodigo] = useState(bodega?.codigo || '')
  const [nombre, setNombre] = useState(bodega?.nombre || '')
  const [direccion, setDireccion] = useState(
    bodega?.direccion || ''
  )
  const [activo, setActivo] = useState(bodega?.activo ?? true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const guardarBodega = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const codigoLimpio = codigo.trim().toUpperCase()
    const nombreLimpio = nombre.trim()

    if (!codigoLimpio) {
      setError('Debes ingresar el código de la bodega.')
      return
    }

    if (!nombreLimpio) {
      setError('Debes ingresar el nombre de la bodega.')
      return
    }

    try {
      setGuardando(true)

      let bodegaGuardada = null

      if (esEdicion) {
        const { data, error: errorActualizar } = await supabase
          .from('bodegas')
          .update({
            codigo: codigoLimpio,
            nombre: nombreLimpio,
            direccion: direccion.trim() || null,
            activo,
          })
          .eq('id', bodega.id)
          .eq('empresa_id', empresaActual.id)
          .select()
          .single()

        if (errorActualizar) {
          throw errorActualizar
        }

        bodegaGuardada = data
      } else {
        const { data, error: errorInsertar } = await supabase
          .from('bodegas')
          .insert({
            empresa_id: empresaActual.id,
            codigo: codigoLimpio,
            nombre: nombreLimpio,
            direccion: direccion.trim() || null,
            activo: true,
          })
          .select()
          .single()

        if (errorInsertar) {
          throw errorInsertar
        }

        bodegaGuardada = data
      }

      onGuardado?.(bodegaGuardada)
    } catch (errorGuardado) {
      console.error('Error al guardar bodega:', errorGuardado)

      if (errorGuardado.code === '23505') {
        setError(
          'Ya existe una bodega con ese código en esta empresa.'
        )
      } else {
        setError(
          errorGuardado.message ||
            'No fue posible guardar la bodega.'
        )
      }
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {esEdicion ? 'Editar bodega' : 'Nueva bodega'}
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="space-y-5"
          onSubmit={guardarBodega}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label
                htmlFor="codigo"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Código
                <span className="ml-1 text-red-500">*</span>
              </label>

              <input
                id="codigo"
                type="text"
                value={codigo}
                onChange={(event) => setCodigo(event.target.value)}
                placeholder="Ejemplo: BOD-01"
                disabled={guardando}
                className={estiloInput}
              />
            </div>

            <div>
              <label
                htmlFor="nombre"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Nombre
                <span className="ml-1 text-red-500">*</span>
              </label>

              <input
                id="nombre"
                type="text"
                value={nombre}
                onChange={(event) => setNombre(event.target.value)}
                placeholder="Ejemplo: Bodega central"
                disabled={guardando}
                className={estiloInput}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="direccion"
              className="mb-2 block text-sm font-semibold text-slate-700"
            >
              Dirección
            </label>

            <input
              id="direccion"
              type="text"
              value={direccion}
              onChange={(event) => setDireccion(event.target.value)}
              placeholder="Dirección de la bodega"
              disabled={guardando}
              className={estiloInput}
            />
          </div>

          {esEdicion && (
            <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={activo}
                onChange={(event) =>
                  setActivo(event.target.checked)
                }
                disabled={guardando}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              Bodega activa
            </label>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default BodegaModal
